import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./ProjectDetails.css";

// Importar los componentes de contenido de cada proyecto
import FlowOptimizationContent from "./content/FlowOptimizationContent";
import StandInContent from "./content/StandInContent";
import DesignSystemContent from "./content/DesignSystemContent";
import Animation3DContent from "./content/Animation3DContent";
import ArtContent from "./content/ArtContent";

// Importar imágenes del hero
import flowOptimizationImg from "../../assets/projects/Dibujos-portfolio.png";
import artHeroImg from "../../assets/projects/Art_project.png";
import DesignSystemImg from "../../assets/projects/DesignSystemHero.png";

import Icon1 from "../../assets/projects/SideBar/ICON1.svg?url";
import Icon2 from "../../assets/projects/SideBar/ICON2.svg?url";
import Icon3 from "../../assets/projects/SideBar/ICON3.svg?url";

const projectsData = {
  flowOptimization: {
    id: "flow-optimization",
    title: "Flow optimization",
    icon: Icon1,
    heroImage: flowOptimizationImg,
    backgroundColor: "#FFF5AB",
    activeColor: "#8F7FEE",
    activeTextColor: "#fff",
    hoverColor: "#D3CCFF",
    role: "Product Designer",
    roleLabel: "Rol in a cross-functional team:",
    objective: "Design an application for Banking Account Executives to streamline and optimize the end-to-end product contracting workflow.",
    company: "BBVA Mexico",
    tools: "Figma, miro",
    ContentComponent: FlowOptimizationContent,
    heroType: "default"
  },
  designSystem: {
    id: "design-system",
    title: "Design System",
    icon: Icon2,
    heroImage: DesignSystemImg,
    backgroundColor: "#EBFFCA",
    activeColor: "#438AF4",
    activeTextColor: "#fff",
    hoverColor: "#AACCFF",
    role: "UX/UI Designer",
    roleLabel: "Role",
    objective: "Develop a design system to improve designers’ efficiency and redesign the core product to enhance its overall accessibility.",
    company: "Todo1 Colombia",
    tools: "Figma, Storybook",
    ContentComponent: DesignSystemContent,
    heroType: "default"
  },
  art: {
    id: "art",
    title: "Art",
    icon: Icon3,
    heroImage: artHeroImg,
    backgroundColor: "transparent",
    activeColor: "#FF8C42",
    activeTextColor: "#fff",
    hoverColor: "#FFF5F0",
    ContentComponent: ArtContent,
    heroType: "fullImage"
  }
};

const projectKeys = Object.keys(projectsData);

export default function ProjectDetails() {
  // ✅ NUEVO: Leer parámetros de la URL
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ✅ NUEVO: Obtener proyecto inicial desde URL o usar default
  const getInitialProject = () => {
    const projectFromUrl = searchParams.get("project");
    if (projectFromUrl && projectKeys.includes(projectFromUrl)) {
      return projectFromUrl;
    }
    return "flowOptimization";
  };

  const [activeProject, setActiveProject] = useState(getInitialProject);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [sidebarPosition, setSidebarPosition] = useState("fixed");
  const sidebarRef = useRef(null);
  
  const project = projectsData[activeProject];
  const ContentComponent = project.ContentComponent;

  // ✅ NUEVO: Sincronizar cuando cambia la URL (por ejemplo, navegación con botones del browser)
  useEffect(() => {
    const projectFromUrl = searchParams.get("project");
    if (projectFromUrl && projectKeys.includes(projectFromUrl)) {
      setActiveProject(projectFromUrl);
    }
  }, [searchParams]);

  // ✅ NUEVO: Función para cambiar proyecto y actualizar URL
  const handleProjectChange = (key) => {
    setActiveProject(key);
    setSearchParams({ project: key });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('.footer');
      if (!footer || !sidebarRef.current) return;

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      if (footerRect.top < windowHeight - 40) {
        setSidebarPosition("absolute");
      } else {
        setSidebarPosition("fixed");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getButtonStyle = (key) => {
    const projectData = projectsData[key];
    const isActive = activeProject === key;
    const isHovered = hoveredProject === key;

    if (isActive) {
      return {
        backgroundColor: projectData.activeColor,
        color: projectData.activeTextColor,
        border: `2px solid black`
      };
    }
    
    if (isHovered) {
      return {
        backgroundColor: projectData.hoverColor,
        color: "#1a1a1a",
        border: "2px solid transparent"
      };
    }

    return {
      backgroundColor: "#fff",
      color: "#1a1a1a",
      border: "2px solid transparent"
    };
  };

  const renderHero = () => {
    if (project.heroType === "fullImage") {
      return (
        <section className="project-hero-fullimage">
          <img src={project.heroImage} alt={project.title} />
          <div className="hero-fullimage-overlay">
            <h2>{project.imageTitle}</h2>
            <p>{project.imageSubtitle}</p>
          </div>
        </section>
      );
    }

    return (
      <section 
        className="project-hero"
        style={{ backgroundColor: project.backgroundColor }}
      >
        <div className="project-hero-content">
          <div className="project-hero-image">
            <img src={project.heroImage} alt={project.title} />
          </div>
          <div className="project-info">
            <div className="info-item">
              <span className="info-label">{project.roleLabel}</span>
              <span className="info-value">{project.role}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Objective:</span>
              <span className="info-value">{project.objective}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Company:</span>
              <span className="info-value">{project.company}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Main tools:</span>
              <span className="info-value">{project.tools}</span>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="project-page">
      <aside 
        ref={sidebarRef}
        className={`project-sidebar ${sidebarPosition}`}
      >
        {projectKeys.map((key) => (
          <button
            key={key}
            className="sidebar-btn"
            style={getButtonStyle(key)}
            onClick={() => handleProjectChange(key)}  // ✅ CAMBIO: usar nueva función
            onMouseEnter={() => setHoveredProject(key)}
            onMouseLeave={() => setHoveredProject(null)}
          >
            <img 
              src={projectsData[key].icon}  
              alt={projectsData[key].title}
              className="sidebar-icon"
              style={{
                filter: activeProject === key ? 'brightness(0) invert(1)' : 'none'
              }}
            />
            <span className="sidebar-text">{projectsData[key].title}</span>
          </button>
        ))}
      </aside>

      {renderHero()}

      <section className={`project-content ${project.heroType === "fullImage" ? "no-hero-padding" : ""}`}>
        <div className="content-wrapper">
          <ContentComponent />
        </div>
      </section>
    </div>
  );
}